/**
 * VoiceWave — equalizer horizontal de barras verticais animadas.
 *
 * Usado no `MobileMicPage` enquanto o `MediaRecorder` está em estado
 * "recording". Estética cativante (inspiração: equalizer iTunes /
 * waveform SoundCloud) para reforçar visualmente que o microfone
 * está capturando áudio.
 *
 * Implementação:
 *   - Cada barra é um `<span>` com altura animada via 5 keyframes
 *     distintos (`vw-bar-1` ... `vw-bar-5`), atribuídos em loop.
 *     Combinado com `animation-delay` escalonado, isso gera a
 *     ilusão orgânica de onda fluida (sem dois vizinhos iguais).
 *   - Quando `active=false`: barras travadas na altura mínima.
 *   - Quando `active=true`: barras dançam.
 *   - Respeita `prefers-reduced-motion` — barras permanecem visíveis
 *     mas paradas (não desaparecem).
 *
 * O CSS é injetado inline via `<style>` no próprio componente para
 * manter o módulo auto-contido e evitar mexer em `global.css`.
 */
import React from 'react';

interface VoiceWaveProps {
  /** Se `true`, barras dançam. Se `false`, permanecem no estado mínimo. */
  readonly active: boolean;
  /** Quantidade de barras. Default: 24. */
  readonly barCount?: number;
  /** Cor das barras. Default: `var(--violet)`. */
  readonly color?: string;
}

/**
 * CSS auto-contido — 5 keyframes diferentes pra criar variedade
 * orgânica. Cada barra cicla 1..5 via `nth-child(5n+k)`.
 *
 * Os pesos das alturas foram calibrados para terem perfil distinto:
 *   - vw-bar-1: pico médio (60%)
 *   - vw-bar-2: pico alto sustentado (95%)
 *   - vw-bar-3: oscilação dupla curta (40-70%)
 *   - vw-bar-4: pico baixo lento (50%)
 *   - vw-bar-5: pico altíssimo rápido (100%)
 */
const WAVE_CSS = `
  .vw-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 3px;
    width: 100%;
    max-width: 280px;
    height: 80px;
    margin: 0 auto;
    padding: 0 8px;
  }
  .vw-bar {
    display: block;
    flex: 1 1 auto;
    min-width: 3px;
    max-width: 8px;
    height: 8%;
    background: var(--vw-color, var(--violet));
    border-radius: 2px;
    box-shadow: 0 0 4px var(--vw-color, var(--violet)),
                0 0 8px rgba(124, 124, 248, 0.35);
    transform-origin: center;
    transition: height 120ms ease-out;
    will-change: height;
  }
  /* Estado ativo — atribui 5 animações diferentes em loop */
  .vw-wrap.is-active .vw-bar:nth-child(5n+1) { animation: vw-bar-1 0.9s ease-in-out infinite; }
  .vw-wrap.is-active .vw-bar:nth-child(5n+2) { animation: vw-bar-2 1.1s ease-in-out infinite; }
  .vw-wrap.is-active .vw-bar:nth-child(5n+3) { animation: vw-bar-3 0.7s ease-in-out infinite; }
  .vw-wrap.is-active .vw-bar:nth-child(5n+4) { animation: vw-bar-4 1.3s ease-in-out infinite; }
  .vw-wrap.is-active .vw-bar:nth-child(5n+5) { animation: vw-bar-5 0.6s ease-in-out infinite; }

  @keyframes vw-bar-1 {
    0%, 100% { height: 12%; }
    50%      { height: 60%; }
  }
  @keyframes vw-bar-2 {
    0%, 100% { height: 18%; }
    40%, 60% { height: 95%; }
  }
  @keyframes vw-bar-3 {
    0%, 100% { height: 10%; }
    25%      { height: 40%; }
    75%      { height: 70%; }
  }
  @keyframes vw-bar-4 {
    0%, 100% { height: 15%; }
    50%      { height: 50%; }
  }
  @keyframes vw-bar-5 {
    0%, 100% { height: 20%; }
    50%      { height: 100%; }
  }

  /* Acessibilidade — não amplificamos o requisito global, mas garantimos
     que as barras fiquem visíveis mesmo paradas. */
  @media (prefers-reduced-motion: reduce) {
    .vw-wrap .vw-bar {
      animation: none !important;
      height: 35% !important;
      opacity: 0.85;
    }
  }
`;

export function VoiceWave({ active, barCount = 24, color }: VoiceWaveProps) {
  // Gera o array de índices sem alocar a cada render desnecessariamente.
  const bars = React.useMemo(
    () => Array.from({ length: Math.max(1, barCount) }, (_, i) => i),
    [barCount]
  );

  // Variável CSS local — permite trocar a cor sem reescrever o stylesheet.
  const wrapStyle: React.CSSProperties = color
    ? ({ ['--vw-color' as string]: color } as React.CSSProperties)
    : {};

  // Delay escalonado por barra — espalha o ciclo pra parecer onda
  // fluindo da esquerda pra direita (sem todos os picos juntos).
  const delayStep = 0.045;

  return (
    <>
      <style>{WAVE_CSS}</style>
      <div
        className={`vw-wrap${active ? ' is-active' : ''}`}
        role="img"
        aria-label={active ? 'Gravando áudio' : 'Microfone parado'}
        style={wrapStyle}
      >
        {bars.map((i) => (
          <span
            key={i}
            className="vw-bar"
            style={{ animationDelay: `${(i * delayStep).toFixed(3)}s` }}
          />
        ))}
      </div>
    </>
  );
}

export default VoiceWave;
